import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  XMarkIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import {
  fetchMeetingMinutesById,
  createMeetingMinutesMeeting,
  fetchMeetingMinutesTemplates,
} from "../../../api/meetingMinutes";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PMCustomSelector } from "../../../components/utils/PMCustomSelector";

export const MeetingMinutesNewMeeting = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meetingMinutes, setMeetingMinutes] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    meeting_date: new Date().toISOString().split('T')[0],
    copy_from_meeting: null,
    template_id: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchMeetingMinutesById(id);
        setMeetingMinutes(data);
        
        const templatesResponse = await fetchMeetingMinutesTemplates({ per_page: 100 });
        setTemplates(templatesResponse.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(true);
        toast.error(t("meeting_minutes.load_error"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Get the next meeting number
      const latestMeetingNumber = meetingMinutes.meetings?.length > 0 
        ? Math.max(...meetingMinutes.meetings.map(m => m.meeting_number || 1))
        : 0;
      
      const payload = {
        meeting_number: latestMeetingNumber + 1,
        meeting_date: formData.meeting_date,
      };

      // Add copy parameters as query params
      const queryParams = {};
      if (formData.copy_from_meeting) {
        queryParams.copy_from_meeting_id = formData.copy_from_meeting;
      } else if (formData.template_id) {
        queryParams.template_id = formData.template_id;
      }
      
      const newMeeting = await createMeetingMinutesMeeting(id, payload, queryParams);
      toast.success(t("meeting_minutes.meeting.created"));
      navigate(`/dashboard/meeting-minutes/${id}/meetings/${newMeeting.id}`);
    } catch (err) {
      console.error("Error creating meeting:", err);
      toast.error(t("meeting_minutes.meeting.create_error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  if (error || !meetingMinutes) {
    return <ResourceError />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={t("meeting_minutes.add_meeting")}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("meeting_minutes.meeting_date")} *
              </label>
              <input
                type="date"
                required
                value={formData.meeting_date}
                onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("meeting_minutes.meeting.copy_options")}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("meeting_minutes.meeting.copy_from_previous")}
                  </label>
                  <select
                    value={formData.copy_from_meeting || ""}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      copy_from_meeting: e.target.value ? parseInt(e.target.value) : null,
                      template_id: null,
                    })}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t("meeting_minutes.meeting.no_copy")}</option>
                    {meetingMinutes.meetings?.map((meeting) => (
                      <option key={meeting.id} value={meeting.id}>
                        {t("meeting_minutes.meeting_number")} {meeting.meeting_number} - {meeting.meeting_date}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="text-center text-gray-500">- {t("meeting_minutes.meeting.or")} -</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("meeting_minutes.templates.use_template")}
                  </label>
                  <select
                    value={formData.template_id || ""}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      template_id: e.target.value ? parseInt(e.target.value) : null,
                      copy_from_meeting: null,
                    })}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t("meeting_minutes.meeting.no_template")}</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/dashboard/meeting-minutes/${id}`)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? t("Saving...") : t("Create")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

